import { RevxMeasureInput } from 'components/RevxMeasureInput';
import { MeasureType } from 'domains/measurementDomain';
import { Form, Input, InputNumber, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { FilterOperatorEnum } from 'domains/appDomain';
import { ParcelCategoryOptions, ParcelFilterObj } from 'domains/parcelDomain';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  normalizeFilterObj,
  normalizeToFormFields,
  normalizeValues,
} from 'utils/filter';
import { FilterOperatorSelect } from '../common/FilterOperatorSelect';
import { FormItemForFilterValues } from '../common/FormItemForFilterValues';
import {
  isSingleOperator,
  MARKUP_OPERATORS,
  NUMERIC_OPERATORS,
} from '../common/utils';
import {
  useMeasurementUnit,
  useRefillFilterOnUnitSwitch,
} from 'hooks/useMeasurementUnit';

export const ParcelFilterForm = () => {
  const [form] = useForm();
  const { tag } = useMeasurementUnit();
  useRefillFilterOnUnitSwitch(form);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filterObj = useAppSelector((state) => state.parcel.filterObj);
  const loading = useAppSelector(
    (state) => state.loading.effects.parcel.getParcels
  );
  const [markupOperator, setMarkupOperator] = useState(
    filterObj.markupPct?.operator
  );

  const onFinish = (values: ParcelFilterObj) => {
    // Tagged here, not at the request: the panel closes and the list re-fetches for every page
    // and sort afterwards, so the unit these numbers were typed in has to travel with them.
    const filterObj = tag(normalizeFilterObj(values));
    dispatch.parcel.saveFilter(filterObj);
  };

  useEffect(() => {
    form.setFieldsValue(normalizeToFormFields(filterObj));
    setMarkupOperator(filterObj.markupPct?.operator);
  }, [filterObj, form]);

  const clearFilters = () => {
    dispatch.parcel.removeFilter();
    form.resetFields();
  };

  return (
    <RevxSpin spinning={loading}>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        onValuesChange={(values) => {
          console.log('onValues Change', values);
        }}
      >
        <Form.Item label={t('platform.filter.formLabel.revxId')}>
          <Form.Item
            noStyle
            name={['revxId', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['revxId', 'values']}>
            <Input allowClear />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.name')}>
          <Form.Item
            noStyle
            name={['name', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['name', 'values']}>
            <Input />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.parcel.columnTitle.category')}>
          <Form.Item
            noStyle
            name={['category', 'operator']}
            initialValue={FilterOperatorEnum.IN}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['category', 'values']}>
            <Select
              mode="multiple"
              showSearch
              filterOption={(value, option) => {
                return String(option?.children)
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase());
              }}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {ParcelCategoryOptions.map((option) => {
                return (
                  <Select.Option value={option.value} key={option.value}>
                    {option.name}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.widthIn')}
          name={['widthIn', 'values']}
          normalize={normalizeValues}
        >
          <RevxMeasureInput
            type={MeasureType.LENGTH}
            addonBefore={
              <Form.Item
                name={['widthIn', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.heightIn')}
          name={['heightIn', 'values']}
          normalize={normalizeValues}
        >
          <RevxMeasureInput
            type={MeasureType.LENGTH}
            addonBefore={
              <Form.Item
                name={['heightIn', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.lengthIn')}
          name={['lengthIn', 'values']}
          normalize={normalizeValues}
        >
          <RevxMeasureInput
            type={MeasureType.LENGTH}
            addonBefore={
              <Form.Item
                name={['lengthIn', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.weightLb')}
          name={['weightLb', 'values']}
          normalize={normalizeValues}
        >
          <RevxMeasureInput
            type={MeasureType.WEIGHT}
            addonBefore={
              <Form.Item
                name={['weightLb', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.priceUSD')}
          name={['price', 'values', '0']}
          normalize={(value) => {
            if (value === '' || value === null) {
              return undefined;
            }
            return value;
          }}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['price', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.markupPCT')}
          name={['markupPct', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            disabled={markupOperator && isSingleOperator(markupOperator)}
            addonBefore={
              <Form.Item
                name={['markupPct', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect
                  operatorOptions={MARKUP_OPERATORS}
                  onChange={(val) => {
                    setMarkupOperator(val as FilterOperatorEnum);

                    if (isSingleOperator(val as FilterOperatorEnum)) {
                      form.setFieldsValue({
                        markupPct: {
                          values: [],
                          operator: val,
                        },
                      });
                    }
                  }}
                />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <RevxButton
              type="primary"
              htmlType="submit"
              brandLightPurple
              className="admin-filter-btn"
            >
              {t('platform.filter.filterBtnLabel')}
            </RevxButton>
            <RevxButton
              onClick={() => {
                clearFilters();
              }}
              className="admin-clear-filter-btn"
              brandGreen
            >
              {t('platform.filter.clearFilterBtnLabel')}
            </RevxButton>
          </Space>
        </Form.Item>
      </Form>
    </RevxSpin>
  );
};
